import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { type Address, getAddress } from 'viem'
import { STRUCTURAL_GC_TIME, STRUCTURAL_STALE_TIME } from '@/config/consts'
import { publicEulerRoutes } from '@/lib/euler-api/publicRouteSpecs'
import type { EulerIndexerToken, NormalizedTokenInfo } from './types'

/**
 * Query key for Euler Indexer tokens
 */
export function eulerTokensQueryKey(chainId: number): readonly [string, string, number] {
  return ['euler', 'tokens', chainId] as const
}

/**
 * Fetch all tokens from Euler Indexer API
 */
async function fetchEulerIndexerTokens(chainId: number): Promise<EulerIndexerToken[]> {
  const url = publicEulerRoutes.tokens.clientUrl(chainId)

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch tokens from Euler Indexer: ${response.status} ${response.statusText}`)
  }

  const data = await response.json()

  if (!Array.isArray(data)) {
    throw new Error('Invalid response format from Euler Indexer')
  }

  return data as EulerIndexerToken[]
}

/**
 * Transform Euler Indexer tokens to normalized format
 */
function normalizeTokens(tokens: EulerIndexerToken[]): Map<Address, NormalizedTokenInfo> {
  const tokenMap = new Map<Address, NormalizedTokenInfo>()

  for (const token of tokens) {
    try {
      const normalizedAddress = getAddress(token.address) as Address
      tokenMap.set(normalizedAddress, {
        chainId: token.chainId,
        address: normalizedAddress,
        name: token.name,
        symbol: token.symbol,
        decimals: token.decimals,
        logoURI: token.logoURI,
        metadata: token.metadata,
        source: 'indexer',
      })
    } catch {
      // Skip invalid addresses
      console.warn(`Invalid token address: ${token.address}`)
    }
  }

  return tokenMap
}

/**
 * Query options for fetching all Euler Indexer tokens
 */
export function eulerTokensQueryOptions(chainId: number) {
  return queryOptions({
    queryKey: eulerTokensQueryKey(chainId),
    queryFn: async () => {
      const tokens = await fetchEulerIndexerTokens(chainId)
      return normalizeTokens(tokens)
    },
    staleTime: STRUCTURAL_STALE_TIME,
    gcTime: STRUCTURAL_GC_TIME,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  })
}

/**
 * Hook to fetch all tokens from Euler Indexer
 */
export function useEulerIndexerTokens(chainId: number) {
  return useQuery(eulerTokensQueryOptions(chainId))
}

/**
 * Hook to get a specific token by address from the cached token list
 * Returns undefined if not found in cache
 */
export function useEulerIndexerToken(chainId: number, address: Address | undefined) {
  const { data: tokenMap } = useEulerIndexerTokens(chainId)

  if (!address || !tokenMap) {
    return undefined
  }

  try {
    const normalizedAddress = getAddress(address) as Address
    return tokenMap.get(normalizedAddress)
  } catch {
    return undefined
  }
}

/**
 * Prefetch Euler Indexer tokens - useful for SSR or initial load
 */
export function usePrefetchEulerTokens(chainId: number) {
  const queryClient = useQueryClient()

  return () => {
    return queryClient.prefetchQuery(eulerTokensQueryOptions(chainId))
  }
}

/**
 * Get token from cache synchronously (for use in callbacks)
 */
export function getTokenFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  chainId: number,
  address: Address,
): NormalizedTokenInfo | undefined {
  const tokenMap = queryClient.getQueryData<Map<Address, NormalizedTokenInfo>>(eulerTokensQueryKey(chainId))

  if (!tokenMap) {
    return undefined
  }

  try {
    const normalizedAddress = getAddress(address) as Address
    return tokenMap.get(normalizedAddress)
  } catch {
    return undefined
  }
}

/**
 * Set token in cache - useful after onchain fallback fetch
 */
export function setTokenInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  chainId: number,
  token: NormalizedTokenInfo,
): void {
  const queryKey = eulerTokensQueryKey(chainId)
  const currentMap = queryClient.getQueryData<Map<Address, NormalizedTokenInfo>>(queryKey)

  if (currentMap) {
    const newMap = new Map(currentMap)
    newMap.set(token.address, token)
    queryClient.setQueryData(queryKey, newMap)
  } else {
    // If no cache exists, create new map with this token
    const newMap = new Map<Address, NormalizedTokenInfo>()
    newMap.set(token.address, token)
    queryClient.setQueryData(queryKey, newMap)
  }
}

